import PropTypes from "prop-types"
import Image from "components/Image"
import htmlFormatting from "utils/htmlFormatting"
import adexinLogo from "icons/logo.png"
import Meta from "components/Meta"
import MicromarkingWrapper from "components/MicromarkingWrapper"
import TestimonialWrapper from "./TestimonialWrapper"
import TestimonialTop from "./TestimonialTop"
import TestimonialPhoto from "./TestimonialPhoto"
import TestimonialUser from "./TestimonialUser"
import TestimonialFlag from "./TestimonialFlag"
import TestimonialTitle from "./TestimonialTitle"
import TestimonialPosition from "./TestimonialPosition"
import TestimonialText from "./TestimonialText"
import TestimonialBottom from "./TestimonialBottom"
import TestimonialLink from "./TestimonialLink"
import useHasMounted from "../../hooks/useHasMounted"

const Testimonial = ({
  name,
  position,
  flag,
  photo,
  text,
  social,
  isGray,
  isFullWidth,
  rate,
  organization,
}) => {
  const hasMounted = useHasMounted()
  if (!hasMounted) {
    return null
  }

  const authorLinkedIn = social.find((item) => item.title.toLowerCase() === "linkedin")

  return (
    <MicromarkingWrapper itemScope itemType="https://schema.org/Review">
      <div itemProp="itemReviewed" itemScope itemType="https://schema.org/Organization">
        <Meta itemProp="name" content="Adexin" />
        <Meta itemProp="image" content={adexinLogo} />
      </div>
      <TestimonialWrapper isFullWidth={isFullWidth} isGray={isGray}>
        <TestimonialTop>
          {!!photo?.alt && (
            <TestimonialPhoto>
              <Image {...photo} objectFit="scale-down" itemProp="image" />
            </TestimonialPhoto>
          )}
          <TestimonialUser showImage={!!photo?.alt}>
            <div>
              {!!photo?.alt && (
                <TestimonialFlag showImage={!!photo?.alt}>
                  <Image {...flag} />
                </TestimonialFlag>
              )}
              <TestimonialTitle itemProp="author" itemScope itemType="https://schema.org/Person">
                <span itemProp="name">{name}</span>
                <Meta itemprop="jobTitle" content={position} />
                {authorLinkedIn?.link && <Meta itemprop="url" content={authorLinkedIn.link} />}
                <MicromarkingWrapper
                  itemProp="affiliation"
                  itemScope
                  itemType="https://schema.org/Organization"
                >
                  <Meta itemProp="name" content={organization} />
                </MicromarkingWrapper>
              </TestimonialTitle>
            </div>
            <TestimonialPosition>
              {position}, {organization}
            </TestimonialPosition>
          </TestimonialUser>
          {!photo?.alt && (
            <TestimonialFlag showImage={!!photo?.alt}>
              <Image {...flag} />
            </TestimonialFlag>
          )}
        </TestimonialTop>
        {text && (
          <TestimonialText
            itemProp="reviewBody"
            dangerouslySetInnerHTML={{
              __html: htmlFormatting(text?.childMarkdownRemark?.html),
            }}
          />
        )}
        {rate > 0 && (
          <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
            <meta itemProp="ratingValue" content={rate} />
          </div>
        )}
        {social && (
          <TestimonialBottom>
            {social.map(({ id, link: socialLink, title }) => (
              <TestimonialLink key={id} to={socialLink}>
                {title}
              </TestimonialLink>
            ))}
          </TestimonialBottom>
        )}
      </TestimonialWrapper>
    </MicromarkingWrapper>
  )
}

Testimonial.propTypes = {
  flag: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  photo: PropTypes.object,
  position: PropTypes.string.isRequired,
  social: PropTypes.array.isRequired,
  text: PropTypes.object.isRequired,
  isGray: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  rate: PropTypes.number,
  organization: PropTypes.string.isRequired,
}

Testimonial.defaultProps = {
  isGray: false,
  isFullWidth: false,
  photo: null,
  rate: 0,
}

export default Testimonial
